import type { CarrierTask, CarrierTrip } from "@/api/carrier";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";

export { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";

const ASSIGNABLE_TASK_STATUSES = new Set(["PENDING", "IN_PROGRESS"]);
const ASSIGNABLE_TRIP_STATUSES = new Set(["PLANNED", "ACTIVE"]);

export interface StagedTasksResult {
  tasks: CarrierTask[];
  dispatchCenterId: string | null;
  rejectedTaskIds: string[];
}

export function taskDispatchCenterId(task: CarrierTask): string | null {
  return task.dispatchCenterId;
}

export function tripDispatchCenterId(trip: CarrierTrip): string | null {
  return trip.dispatchCenterId ?? null;
}

export function isTaskSelectableForStaging(task: CarrierTask): boolean {
  return (
    !task.tripId &&
    (!task.investigation || task.investigation.state === "RECOVERED") &&
    !isTaskOnCancellationHold(task) &&
    Boolean(taskDispatchCenterId(task)) &&
    ASSIGNABLE_TASK_STATUSES.has(task.status.toUpperCase())
  );
}

export function stagedDispatchCenterId(tasks: CarrierTask[]): string | null {
  return tasks.length > 0 ? taskDispatchCenterId(tasks[0]) : null;
}

export function stageTasks(
  current: CarrierTask[],
  candidates: CarrierTask[]
): StagedTasksResult {
  const tasks = Array.from(
    new Map(current.map((task) => [task.id, task])).values()
  );
  const existingIds = new Set(tasks.map((task) => task.id));
  let dispatchCenterId = stagedDispatchCenterId(tasks);
  const rejectedTaskIds: string[] = [];

  for (const candidate of candidates) {
    if (existingIds.has(candidate.id)) continue;
    const candidateCenterId = taskDispatchCenterId(candidate);
    if (!isTaskSelectableForStaging(candidate)) {
      rejectedTaskIds.push(candidate.id);
      continue;
    }
    dispatchCenterId ??= candidateCenterId;
    if (candidateCenterId !== dispatchCenterId) {
      rejectedTaskIds.push(candidate.id);
      continue;
    }
    existingIds.add(candidate.id);
    tasks.push(candidate);
  }

  return { tasks, dispatchCenterId, rejectedTaskIds };
}

export function removeStagedTask(
  current: CarrierTask[],
  taskId: string
): StagedTasksResult {
  const tasks = current.filter((task) => task.id !== taskId);
  return {
    tasks,
    dispatchCenterId: stagedDispatchCenterId(tasks),
    rejectedTaskIds: [],
  };
}

export function compatibleAssignmentTrips(
  trips: CarrierTrip[],
  dispatchCenterId: string | null
): CarrierTrip[] {
  if (!dispatchCenterId) return [];
  return trips.filter(
    (trip) =>
      tripDispatchCenterId(trip) === dispatchCenterId &&
      ASSIGNABLE_TRIP_STATUSES.has(trip.status.toUpperCase())
  );
}
